import { useState, useEffect, useCallback, useMemo } from 'react';
import api, { formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Settings, CreditCard, Phone, Globe, Building2, Eye, EyeOff, CheckCircle, XCircle, Loader2, Save, KeyRound, Trash2, Mail, ShieldCheck, AlertTriangle, RefreshCw, Database, Eraser } from 'lucide-react';
import { HighVisibilityPageHeader } from '@/components/ui/high-visibility-mobile';

const categoryConfig = {
  general: { label: 'Company Info', icon: Building2, color: 'text-zinc-700' },
  payment: { label: 'Payment Processors', icon: CreditCard, color: 'text-emerald-600' },
  communication: { label: 'Communication', icon: Phone, color: 'text-blue-700' },
  google: { label: 'Google Services', icon: Globe, color: 'text-red-500' },
};

const getSettingValue = (settings, editValues, key) => editValues[key] ?? settings.find(s => s.key === key)?.value ?? '';
const formatCleanupExample = (example) => Object.entries(example).filter(([key]) => key !== 'id').map(([key, value]) => `${key}: ${value}`).join(' · ');

function EmailReadinessPanel({ settings, editValues, testResults, testing, onTest }) {
  const enabled = String(getSettingValue(settings, editValues, 'EMAIL_SENDING_ENABLED') || 'false').toLowerCase() === 'true';
  const fromName = getSettingValue(settings, editValues, 'EMAIL_FROM_NAME');
  const fromEmail = getSettingValue(settings, editValues, 'EMAIL_FROM_EMAIL');
  const replyTo = getSettingValue(settings, editValues, 'EMAIL_REPLY_TO');
  const domain = getSettingValue(settings, editValues, 'EMAIL_SENDER_DOMAIN');
  const result = testResults.email;
  const providerSetting = settings.find(s => s.key === 'RESEND_API_KEY') || settings.find(s => s.key === 'SENDGRID_API_KEY');
  const identityReady = result?.identity_ready ?? Boolean(fromName && fromEmail);
  const providerReady = result?.provider_ready ?? Boolean(providerSetting?.has_value);
  const providerLabel = result?.provider && result.provider !== 'none' ? result.provider : (providerReady ? 'Provider key present' : 'No live provider configured');
  const status = enabled ? (identityReady && providerReady ? 'ready' : 'needs-setup') : 'logged-only';
  const statusClass = status === 'ready' ? 'border border-emerald-200 bg-emerald-50 text-emerald-700' : status === 'needs-setup' ? 'border border-amber-200 bg-amber-50 text-amber-700' : 'border border-blue-200 bg-blue-50 text-blue-700';

  return (
    <Card className="overflow-hidden rounded-[1.5rem] border border-blue-100 bg-white shadow-sm sm:rounded-md" data-testid="email-readiness-panel">
      <CardHeader className="pb-3 border-b border-blue-50 bg-white">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-xl font-black font-heading flex items-center gap-2 sm:text-base sm:font-bold" data-testid="email-readiness-title">
              <Mail className="w-5 h-5 text-blue-600 sm:w-4 sm:h-4" /> Email Readiness
            </CardTitle>
            <p className="text-xs text-zinc-500 mt-1" data-testid="email-readiness-subtitle">Sender identity, disabled/live state, and provider readiness</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge className={statusClass} data-testid="email-readiness-status">{status === 'ready' ? 'Ready' : status === 'needs-setup' ? 'Needs setup' : 'Logged only'}</Badge>
            <Button type="button" variant="outline" className="h-12 rounded-2xl border-blue-100 bg-blue-50 font-black text-blue-700 sm:h-9 sm:rounded-sm" onClick={() => onTest('email')} disabled={testing === 'email'} data-testid="test-email-readiness">
              {testing === 'email' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
              Check email
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {result && (
          <div className={`flex items-start gap-2 rounded-sm border p-3 text-xs ${result.status === 'configured' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : result.status === 'disabled' ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-amber-200 bg-amber-50 text-amber-800'}`} data-testid="email-readiness-result">
            {result.status === 'configured' ? <CheckCircle className="w-4 h-4 mt-0.5" /> : <AlertTriangle className="w-4 h-4 mt-0.5" />}
            <span>{result.message}</span>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2" data-testid="email-readiness-grid">
          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 sm:rounded-sm sm:bg-white" data-testid="email-readiness-identity">
            <p className="authority-overline">Sender identity</p>
            <p className="mt-1 text-sm font-semibold text-zinc-900">{fromName || 'No sender name'}</p>
            <p className="text-xs text-zinc-500">{fromEmail || 'No from email yet'}</p>
            {replyTo && <p className="text-xs text-zinc-500">Reply-to: {replyTo}</p>}
          </div>
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 sm:rounded-sm sm:bg-white" data-testid="email-readiness-provider">
            <p className="authority-overline">Provider</p>
            <p className="mt-1 text-sm font-semibold text-zinc-900">{providerLabel}</p>
            <p className="text-xs text-zinc-500">Current mode: {enabled ? 'Live requested' : 'Disabled / logging only'}</p>
          </div>
          <div className="rounded-2xl border border-violet-100 bg-violet-50 p-4 sm:rounded-sm sm:bg-white" data-testid="email-readiness-domain">
            <p className="authority-overline">Sender domain/brand</p>
            <p className="mt-1 text-sm font-semibold text-zinc-900">{domain || 'Not set'}</p>
            <p className="text-xs text-zinc-500">DNS verification comes with the live provider step.</p>
          </div>
          <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4 sm:rounded-sm sm:bg-white" data-testid="email-readiness-events">
            <p className="authority-overline">Logged events</p>
            <p className="mt-1 text-sm font-semibold text-zinc-900">Lead, quote, invoice, job, review, proof</p>
            <p className="text-xs text-zinc-500">Aligned with the current SMS templates.</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EmailDeliverySettingsPanel({ settings, editValues, showSecrets, saving, emailTestResult, emailTestSending, onChange, onToggleSecret, onSave, onSendTestEmail }) {
  const fields = [
    { key: 'EMAIL_SENDING_ENABLED', label: 'Live Email Sending', description: 'Set true to send live emails; false logs email events only.', type: 'select' },
    { key: 'RESEND_API_KEY', label: 'Resend API Key', description: 'Resend API key starting with re_. Keep this secret.', secret: true },
    { key: 'EMAIL_FROM_NAME', label: 'From Name', description: 'Display name recipients see in their inbox.' },
    { key: 'EMAIL_FROM_EMAIL', label: 'From Email', description: 'Must use the verified Resend sending domain/subdomain.' },
    { key: 'EMAIL_REPLY_TO', label: 'Reply-To Email', description: 'Replies go here, usually your Zoho inbox.' },
    { key: 'INTERNAL_NOTIFICATION_EMAILS', label: 'Internal Visibility Emails', description: 'Comma-separated extra recipients for staff/internal notifications.' },
    { key: 'EMAIL_SENDER_DOMAIN', label: 'Verified Sender Domain', description: 'The Resend verified domain/subdomain.' },
    { key: 'NEW_LEAD_ALERT_EMAILS', label: 'New Lead Alert Recipients', description: 'Comma-separated emails that receive new form submission alerts.' },
  ];
  const settingLookup = Object.fromEntries(settings.map(item => [item.key, item]));
  const getValue = (key) => editValues[key] ?? settingLookup[key]?.value ?? '';

  return (
    <Card className="rounded-[1.5rem] border border-blue-100 bg-white shadow-sm sm:rounded-md" data-testid="email-delivery-settings-panel">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-xl font-black font-heading flex items-center gap-2 sm:text-base sm:font-bold" data-testid="email-delivery-settings-title">
              <Mail className="w-5 h-5 text-blue-600 sm:w-4 sm:h-4" /> Email Delivery Settings
            </CardTitle>
            <p className="text-xs text-zinc-500 mt-1">Edit Resend, sender, reply-to, alert recipients, and live-send mode.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" className="h-12 rounded-2xl border-blue-100 bg-blue-50 font-black text-blue-700 sm:h-9 sm:rounded-sm" onClick={onSendTestEmail} disabled={emailTestSending} data-testid="send-test-email-button">
              {emailTestSending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />} Send Test Email
            </Button>
            <Button className="h-12 rounded-2xl bg-blue-600 font-black text-white hover:bg-blue-700 sm:h-9 sm:rounded-sm" onClick={() => onSave(fields.map(field => field.key), 'email delivery settings')} disabled={saving} data-testid="save-email-delivery-settings">
              <Save className="w-3 h-3 mr-1" /> Save Email Settings
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        {emailTestResult && (
          <div className={`sm:col-span-2 rounded-sm border p-3 text-sm ${emailTestResult.status === 'sent' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'}`} data-testid="send-test-email-result">
            <span className="font-semibold">Test email:</span> {emailTestResult.status} {emailTestResult.to ? `to ${emailTestResult.to}` : ''}{emailTestResult.provider_id ? ` · ${emailTestResult.provider_id}` : ''}{emailTestResult.message ? ` · ${emailTestResult.message}` : ''}
          </div>
        )}
        {fields.map(field => {
          const setting = settingLookup[field.key] || {};
          const isSecret = field.secret || setting.is_secret;
          return (
            <div key={field.key} className={['NEW_LEAD_ALERT_EMAILS', 'INTERNAL_NOTIFICATION_EMAILS'].includes(field.key) ? 'sm:col-span-2' : ''} data-testid={`email-field-${field.key}`}>
              <div className="mb-1 flex items-center justify-between gap-2">
                <Label className="text-sm font-medium">{field.label}</Label>
                <div className="flex items-center gap-1.5">
                  {setting.has_value && <Badge variant="secondary" className="border border-blue-200 bg-blue-50 text-[10px] text-blue-700" data-testid={`email-field-source-${field.key}`}>{setting.source || 'saved'}</Badge>}
                  {isSecret && (
                    <button type="button" onClick={() => onToggleSecret(field.key)} className="text-zinc-400 hover:text-zinc-600" data-testid={`email-field-toggle-secret-${field.key}`}>
                      {showSecrets[field.key] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              </div>
              {field.type === 'select' ? (
                <select value={getValue(field.key) || 'false'} onChange={(event) => onChange(field.key, event.target.value)} className="h-12 w-full rounded-2xl border border-blue-100 bg-blue-50 px-3 text-base font-bold text-blue-900 sm:h-9 sm:rounded-sm sm:text-sm sm:font-normal" data-testid={`email-field-input-${field.key}`}>
                  <option value="true">true — send live emails</option>
                  <option value="false">false — log only</option>
                </select>
              ) : (
                <Input
                  type={isSecret && !showSecrets[field.key] ? 'password' : 'text'}
                  value={getValue(field.key)}
                  onChange={(event) => onChange(field.key, event.target.value)}
                  placeholder={field.description}
                  className="h-12 rounded-2xl border-blue-100 text-base font-semibold sm:h-9 sm:rounded-sm sm:text-sm"
                  data-testid={`email-field-input-${field.key}`}
                />
              )}
              <p className="mt-0.5 text-[11px] text-zinc-400">{field.description}</p>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function SampleDataCleanupPanel() {
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/maintenance/sample-data/preview');
      setPreview(data);
      setSelected(new Set((data.available_collections || []).map(item => item.name).filter(name => (data.collections?.[name]?.count || 0) > 0)));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const toggleSelected = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
    setResult(null);
  };

  const selectedTotal = useMemo(() => Array.from(selected).reduce((sum, name) => sum + (preview?.collections?.[name]?.count || 0), 0), [selected, preview]);

  const pruneSelected = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const { data } = await api.post('/maintenance/sample-data/prune', {
        confirm: confirmText,
        collections: Array.from(selected),
      });
      setResult(data);
      setConfirmText('');
      await fetchPreview();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="rounded-[1.5rem] border border-orange-100 bg-white shadow-sm sm:rounded-md" data-testid="sample-cleanup-panel">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-xl font-black font-heading flex items-center gap-2 sm:text-base sm:font-bold" data-testid="sample-cleanup-title">
              <Database className="w-5 h-5 text-orange-600 sm:w-4 sm:h-4" /> Sample Data Cleanup
            </CardTitle>
            <p className="text-xs text-zinc-500 mt-1">Preview and remove obvious records matching test, pytest, smoke, demo, sample, or preview.</p>
          </div>
          <Button type="button" variant="outline" className="h-12 rounded-2xl border-orange-100 bg-orange-50 font-black text-orange-700 sm:h-9 sm:rounded-sm" onClick={fetchPreview} disabled={loading} data-testid="sample-cleanup-refresh">
            {loading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />} Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <div className="rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="sample-cleanup-error">{error}</div>}
        {result && <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700" data-testid="sample-cleanup-result">Deleted {result.total_deleted || 0} records.</div>}
        <div className="grid gap-2" data-testid="sample-cleanup-sections">
          {(preview?.available_collections || []).map(item => {
            const info = preview?.collections?.[item.name] || { count: 0, examples: [] };
            const checked = selected.has(item.name);
            return (
              <div key={item.name} className="rounded-2xl border border-orange-100 bg-orange-50 p-3 sm:rounded-sm sm:bg-white sm:border-zinc-200" data-testid={`sample-cleanup-section-${item.name}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={checked} onChange={() => toggleSelected(item.name)} className="mt-1 h-4 w-4" data-testid={`sample-cleanup-checkbox-${item.name}`} />
                  <span className="flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-zinc-900">{item.label}</span>
                      <Badge variant="secondary" className={info.count > 0 ? 'bg-amber-50 text-amber-700' : 'bg-zinc-100 text-zinc-500'} data-testid={`sample-cleanup-count-${item.name}`}>{info.count || 0} matches</Badge>
                    </span>
                      <span className="mt-1 block text-[11px] text-zinc-500">Fields: {item.fields.join(', ')}</span>
                    {info.examples?.length > 0 && (
                      <span className="mt-2 block space-y-1" data-testid={`sample-cleanup-examples-${item.name}`}>
                        {info.examples.slice(0, 3).map(example => (
                          <span key={example.id} className="block rounded bg-zinc-50 px-2 py-1 text-[11px] text-zinc-600 truncate">
                            {formatCleanupExample(example)}
                          </span>
                        ))}
                      </span>
                    )}
                  </span>
                </label>
              </div>
            );
          })}
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:rounded-sm" data-testid="sample-cleanup-confirm-panel">
          <div className="flex items-start gap-2 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4" />
            <p><span className="font-semibold">Selected cleanup:</span> {selectedTotal} matching records across {selected.size} sections. Type <code className="rounded bg-white px-1">DELETE_SAMPLE_DATA</code> to permanently delete selected matches.</p>
          </div>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE_SAMPLE_DATA" className="h-12 rounded-2xl sm:h-9 sm:rounded-sm" data-testid="sample-cleanup-confirm-input" />
            <Button type="button" onClick={pruneSelected} disabled={loading || selected.size === 0 || confirmText !== 'DELETE_SAMPLE_DATA'} className="h-12 rounded-2xl bg-red-600 font-black hover:bg-red-700 sm:h-9 sm:rounded-sm" data-testid="sample-cleanup-delete-button">
              {loading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Eraser className="mr-1 h-3.5 w-3.5" />} Delete Selected
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editValues, setEditValues] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [saving, setSaving] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting] = useState('');
  const [emailTestSending, setEmailTestSending] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);
  const [saveMsg, setSaveMsg] = useState('');
  const [serviceKeys, setServiceKeys] = useState([]);
  const [newKey, setNewKey] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await api.get('/settings');
      setSettings(data);
      const vals = {};
      data.forEach(s => { vals[s.key] = s.value || ''; });
      setEditValues(vals);
    } catch { return; }
    finally { setLoading(false); }
  }, []);

  const fetchServiceKeys = useCallback(async () => {
    try {
      const { data } = await api.get('/service-keys');
      setServiceKeys(data);
    } catch { return; }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => { fetchServiceKeys(); }, [fetchServiceKeys]);

  const handleChange = useCallback((key, value) => {
    setEditValues(prev => ({ ...prev, [key]: value }));
    setSaveMsg('');
  }, []);

  const toggleSecret = useCallback((key) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleSave = useCallback(async (category) => {
    setSaving(true);
    setSaveMsg('');
    const categoryKeys = settings.filter(s => s.category === category).map(s => s.key);
    const toUpdate = {};
    categoryKeys.forEach(k => { toUpdate[k] = editValues[k] || ''; });
    try {
      const { data } = await api.put('/settings', { settings: toUpdate });
      setSaveMsg(`Saved ${data.updated?.length || 0} settings`);
      fetchSettings();
    } catch (err) {
      setSaveMsg(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  }, [editValues, fetchSettings, settings]);

  const handleSaveKeys = useCallback(async (keys, label = 'settings') => {
    setSaving(true);
    setSaveMsg('');
    const toUpdate = {};
    keys.forEach(k => { toUpdate[k] = editValues[k] || ''; });
    try {
      const { data } = await api.put('/settings', { settings: toUpdate });
      setSaveMsg(`Saved ${data.updated?.length || 0} ${label}`);
      fetchSettings();
    } catch (err) {
      setSaveMsg(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  }, [editValues, fetchSettings]);

  const testConnection = useCallback(async (provider) => {
    setTesting(provider);
    setTestResults(prev => ({ ...prev, [provider]: null }));
    try {
      const { data } = await api.post('/settings/test-connection', { provider });
      setTestResults(prev => ({ ...prev, ...data }));
    } catch (err) {
      setTestResults(prev => ({ ...prev, [provider]: { status: 'error', message: 'Test failed' } }));
    } finally { setTesting(''); }
  }, []);

  const sendTestEmail = useCallback(async () => {
    setEmailTestSending(true);
    setEmailTestResult(null);
    setSaveMsg('');
    try {
      await handleSaveKeys(['EMAIL_SENDING_ENABLED', 'RESEND_API_KEY', 'EMAIL_FROM_NAME', 'EMAIL_FROM_EMAIL', 'EMAIL_REPLY_TO', 'EMAIL_SENDER_DOMAIN', 'NEW_LEAD_ALERT_EMAILS', 'INTERNAL_NOTIFICATION_EMAILS'], 'email delivery settings');
      const firstRecipient = (editValues.NEW_LEAD_ALERT_EMAILS || '').split(/[;,\n]/).map(item => item.trim()).filter(Boolean)[0] || '';
      const { data } = await api.post('/settings/email-test', { to_email: firstRecipient });
      setEmailTestResult(data);
    } catch (err) {
      setEmailTestResult({ status: 'error', message: formatApiError(err.response?.data?.detail) });
    } finally {
      setEmailTestSending(false);
      fetchSettings();
    }
  }, [editValues, fetchSettings, handleSaveKeys]);

  const createServiceKey = useCallback(async (e) => {
    e.preventDefault();
    setSaveMsg('');
    const f = new FormData(e.target);
    try {
      const { data } = await api.post('/service-keys', {
        name: f.get('name'),
        purpose: 'external_leads',
        rate_limit_per_minute: parseInt(f.get('rate_limit_per_minute') || '60', 10),
      });
      setNewKey(data);
      e.target.reset();
      fetchServiceKeys();
    } catch (err) {
      setSaveMsg(formatApiError(err.response?.data?.detail));
    }
  }, [fetchServiceKeys]);

  const revokeServiceKey = useCallback(async (keyId) => {
    if (!window.confirm('Revoke this API key? Existing senders using it will fail immediately.')) return;
    await api.delete(`/service-keys/${keyId}`);
    fetchServiceKeys();
  }, [fetchServiceKeys]);

  const categories = ['general', 'payment', 'communication', 'google'];
  const emailDeliveryKeys = new Set(['EMAIL_SENDING_ENABLED', 'RESEND_API_KEY', 'EMAIL_FROM_NAME', 'EMAIL_FROM_EMAIL', 'EMAIL_REPLY_TO', 'EMAIL_SENDER_DOMAIN', 'NEW_LEAD_ALERT_EMAILS', 'INTERNAL_NOTIFICATION_EMAILS']);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-40 rounded-[1.75rem] bg-blue-100" />
        {[1,2,3].map(i => <div key={i} className="h-48 rounded-[1.5rem] bg-blue-100" />)}
      </div>
    );
  }

  return (
    <div className="-mx-3 space-y-6 bg-[#eef7ff] px-0 pb-8 pt-0 animate-fade-in sm:mx-0 sm:max-w-5xl sm:bg-transparent sm:px-0 sm:pt-0">
      <HighVisibilityPageHeader
        eyebrow="System controls"
        title="Settings"
        subtitle="Manage integrations, API keys, company identity, and cleanup tools."
        countLabel={`${settings.length} settings · ${serviceKeys.length} service keys`}
        icon={Settings}
        tone="violet"
        testId="settings-high-visibility-header"
        titleTestId="settings-title"
      />

      <div className="space-y-6 px-3 sm:px-0">

      {saveMsg && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-base font-bold text-emerald-700 sm:rounded-sm" data-testid="settings-save-msg">
          {saveMsg}
        </div>
      )}

      <EmailReadinessPanel settings={settings} editValues={editValues} testResults={testResults} testing={testing} onTest={testConnection} />

      <EmailDeliverySettingsPanel
        settings={settings}
        editValues={editValues}
        showSecrets={showSecrets}
        saving={saving}
        emailTestResult={emailTestResult}
        emailTestSending={emailTestSending}
        onChange={handleChange}
        onToggleSecret={toggleSecret}
        onSave={handleSaveKeys}
        onSendTestEmail={sendTestEmail}
      />

      <SampleDataCleanupPanel />

      {categories.map(cat => {
        const config = categoryConfig[cat] || { label: cat, icon: Settings, color: 'text-zinc-600' };
        const Icon = config.icon;
        const catSettings = settings.filter(s => !(cat === 'communication' && emailDeliveryKeys.has(s.key)) && s.category === cat);
        if (catSettings.length === 0) return null;

        const testableProviders = cat === 'payment' ? ['square', 'stripe'] : cat === 'communication' ? ['twilio', 'email'] : [];

        return (
          <Card key={cat} className="rounded-[1.5rem] border border-blue-100 bg-white shadow-sm sm:rounded-md" data-testid={`settings-category-${cat}`}>
            <CardHeader className="pb-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-xl font-black font-heading flex items-center gap-2 sm:text-base sm:font-bold">
                  <Icon className={`w-5 h-5 ${config.color} sm:w-4 sm:h-4`} />
                  {config.label}
                </CardTitle>
                <div className="flex flex-wrap gap-2">
                  {testableProviders.map(provider => (
                    <Button key={provider} variant="outline" className="h-12 rounded-2xl border-blue-100 bg-blue-50 text-sm font-black text-blue-700 sm:h-9 sm:rounded-sm sm:text-xs"
                      onClick={() => testConnection(provider)}
                      disabled={testing === provider}
                      data-testid={`test-${provider}`}>
                      {testing === provider ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                      Test {provider}
                    </Button>
                  ))}
                  <Button className="h-12 rounded-2xl bg-blue-600 text-sm font-black text-white hover:bg-blue-700 sm:h-9 sm:rounded-sm sm:text-xs" onClick={() => handleSave(cat)} disabled={saving} data-testid={`save-${cat}`}>
                    <Save className="w-3 h-3 mr-1" /> Save
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Test results */}
              {testableProviders.map(provider => {
                const res = testResults[provider];
                if (!res) return null;
                return (
                  <div key={provider} className={`flex items-center gap-2 rounded-sm border p-2.5 text-xs ${res.status === 'connected' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : res.status === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}
                    data-testid={`test-result-${provider}`}>
                    {res.status === 'connected' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    <span className="font-medium capitalize">{provider}:</span> {res.message}
                  </div>
                );
              })}

              {catSettings.map(setting => (
                <div key={setting.key} data-testid={`setting-${setting.key}`}>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-sm font-medium">{setting.label}</Label>
                    <div className="flex items-center gap-1.5">
                      {setting.has_value && (
                        <Badge variant="secondary" className={`text-[10px] ${setting.source === 'database' ? 'border border-blue-200 bg-blue-50 text-blue-700' : 'bg-zinc-100 text-zinc-500'}`}>
                          {setting.source}
                        </Badge>
                      )}
                      {setting.is_secret && (
                        <button onClick={() => toggleSecret(setting.key)} className="text-zinc-400 hover:text-zinc-600" data-testid={`toggle-secret-${setting.key}`}>
                          {showSecrets[setting.key] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                      )}
                    </div>
                  </div>
                  <Input
                    type={setting.is_secret && !showSecrets[setting.key] ? 'password' : 'text'}
                    value={editValues[setting.key] || ''}
                    onChange={(e) => handleChange(setting.key, e.target.value)}
                    placeholder={setting.description}
                    className="h-12 rounded-2xl border-blue-100 text-base font-semibold sm:h-9 sm:rounded-sm sm:text-sm"
                    data-testid={`input-${setting.key}`}
                  />
                  <p className="text-[11px] text-zinc-400 mt-0.5">{setting.description}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })}

      <Card className="rounded-[1.5rem] border border-violet-100 bg-white shadow-sm sm:rounded-md" data-testid="settings-category-service-keys">
        <CardHeader className="pb-3">
          <CardTitle className="text-xl font-black font-heading flex items-center gap-2 sm:text-base sm:font-bold">
            <KeyRound className="w-5 h-5 text-violet-700 sm:w-4 sm:h-4" /> External Lead API Keys
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-2xl border border-violet-100 bg-violet-50 p-4 text-sm font-semibold text-zinc-700 sm:rounded-sm sm:text-xs" data-testid="external-lead-api-docs">
            <p className="font-semibold text-zinc-800">Landing page connector</p>
            <p className="mt-1">Endpoint: <code className="rounded bg-white px-1">POST /api/external/leads</code></p>
            <p>Auth header: <code className="rounded bg-white px-1">X-API-Key</code></p>
            <p>GMV example: <code className="rounded bg-white px-1">dba_slug=butler-county-junk-removal</code>, <code className="rounded bg-white px-1">source=gmv-llc-landing</code>, <code className="rounded bg-white px-1">service_category=junk-removal</code></p>
            <p className="mt-1">Full guide: <code className="rounded bg-white px-1">/app/EXTERNAL_LEAD_API.md</code></p>
          </div>
          {newKey && (
            <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="new-service-key-alert">
              <p className="font-semibold">Copy this key now. It is shown only once in full.</p>
              <code className="mt-2 block break-all rounded bg-white p-2 text-xs" data-testid="new-service-key-value">{newKey.key}</code>
            </div>
          )}
          <form onSubmit={createServiceKey} className="grid gap-3 sm:grid-cols-[1fr_140px_auto]" data-testid="create-service-key-form">
            <div>
              <Label>Name</Label>
              <Input name="name" placeholder="Landing page sender" required className="h-12 rounded-2xl sm:h-9 sm:rounded-sm" data-testid="service-key-name-input" />
            </div>
            <div>
              <Label>RPM</Label>
              <Input name="rate_limit_per_minute" type="number" min="1" defaultValue="60" className="h-12 rounded-2xl sm:h-9 sm:rounded-sm" data-testid="service-key-rate-input" />
            </div>
            <div className="flex items-end">
              <Button type="submit" className="h-12 rounded-2xl bg-violet-600 font-black text-white hover:bg-violet-700 sm:h-9 sm:rounded-sm" data-testid="create-service-key-button"><KeyRound className="w-4 h-4 mr-1" /> Create</Button>
            </div>
          </form>
          <div className="space-y-2" data-testid="service-key-list">
            {serviceKeys.length === 0 ? (
              <p className="text-sm text-zinc-500" data-testid="service-key-empty">No service keys yet.</p>
            ) : serviceKeys.map(key => (
              <div key={key.id} className="flex flex-col gap-3 rounded-2xl border border-violet-100 bg-violet-50 p-4 text-sm sm:flex-row sm:items-center sm:justify-between sm:rounded-sm sm:border-zinc-200 sm:bg-white sm:p-3" data-testid={`service-key-row-${key.id}`}>
                <div>
                  <p className="font-medium" data-testid={`service-key-name-${key.id}`}>{key.name}</p>
                  <p className="text-xs text-zinc-500" data-testid={`service-key-value-${key.id}`}>{key.key} · {key.purpose || 'external_leads'} · {key.rate_limit_per_minute || 60} rpm</p>
                </div>
                <Button variant="outline" className="h-12 rounded-2xl border-red-100 bg-red-50 text-red-600 sm:h-9 sm:rounded-sm" onClick={() => revokeServiceKey(key.id)} data-testid={`revoke-service-key-${key.id}`} aria-label="Revoke service key">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
